import React from "react";
import Modal from "react-native-modal";
import ResponsiveContent from "../shared/responsive-content";
import { View, Pressable, Text } from "react-native";
import CloseIcon from "@/assets/svgs/close-icon";
import { Button } from "../ui/button";
import { Textarea } from "../ui/textarea";
import { Controller, useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";

const ReviewRejectRequestFormSchema = z.object({
  reason: z.string().min(1),
});

function RequestRejectedModal({
  isVisible,
  onClose,
  onDismiss,
}: {
  isVisible: boolean;
  onClose: () => void;
  onDismiss: (reason?: string) => void;
}) {
  const { control, handleSubmit, formState } = useForm({
    defaultValues: {
      reason: "",
    },
    resolver: zodResolver(ReviewRejectRequestFormSchema),
    mode: "onChange",
  }); 

  const onReject = (data: z.infer<typeof ReviewRejectRequestFormSchema>) => {
    onDismiss(data.reason);
  };

  return (
    <Modal
      animationIn="slideInUp"
      animationOut="slideOutDown"
      isVisible={isVisible}
      onBackdropPress={onClose}
      onDismiss={() => {
        onDismiss();
      }}
      useNativeDriver
      useNativeDriverForBackdrop
      hideModalContentWhileAnimating
    >
      <ResponsiveContent>
        <View className="mx-4 bg-teachLoop-creamBase rounded-xl px-8 py-10">
          <View className="items-end">
            <Pressable
              onPress={onClose}
              className="active:opacity-80"
              style={{ marginHorizontal: -12 }}
            >
              <CloseIcon
                pathClassName="stroke-teachLoop-textPrimary"
                className="size-6"
              />
            </Pressable>
          </View>
          <View className="gap-8">
            <Text className="font-zilla-slab-medium font-medium text-[28px] leading-[1.2] text-black">
              Are you sure want to decline the request?
            </Text>
            <Text className="text-base font-geologica-regular font-normal leading-[1.5] text-teachLoop-textPrimary">
              Please provide a reason for declining the request
            </Text>

            <Controller
              control={control}
              name="reason"
              render={({ field, fieldState: { error } }) => (
                <Textarea
                  placeholder="Please provide a short reason for your cancellation."
                  className="h-[167]"
                  size="sm"
                  multiline
                  numberOfLines={4}
                  textAlignVertical="top"
                  {...field}
                  error={!!error?.message}
                />
              )}
            />

            <Text className="text-base font-geologica-regular font-normal leading-[1.5] text-teachLoop-textPrimary">
              This action cannot be undone. Once cancelled, a notification will
              be sent to the Parent.
            </Text>
            <Button
              fullWidth
              size="md"
              intent="danger"
              className="bg-red-600"
              onPress={handleSubmit(onReject)}
              disabled={!formState.isValid}
            >
              <Button.Text size="md">Decline request</Button.Text>
            </Button>
          </View>
          <View className="h-3" />
        </View>
      </ResponsiveContent>
    </Modal>
  );
}

export default RequestRejectedModal;
